// confirmToast.tsx
import React, { useEffect } from 'react';
import { createRoot, Root } from 'react-dom/client';

interface ConfirmModalProps {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({ message, onConfirm, onCancel }) => {
  useEffect(() => {
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[9999] bg-black bg-opacity-50">
      <div className="bg-white shadow-xl rounded-lg p-6 flex flex-col gap-4 w-80 max-w-sm mx-4">
        <p className="text-gray-800 font-medium text-center">{message}</p>
        <div className="flex justify-center gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm rounded-md bg-gray-200 hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm rounded-md bg-red-500 text-white hover:bg-red-600 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// Global state to manage modals
let modalContainer: HTMLDivElement | null = null;
let root: Root | null = null;

const createModalContainer = () => {
  if (!modalContainer) {
    modalContainer = document.createElement('div');
    modalContainer.id = 'confirm-modal-root';
    modalContainer.style.position = 'fixed';
    modalContainer.style.top = '0';
    modalContainer.style.left = '0';
    modalContainer.style.width = '100%';
    modalContainer.style.height = '100%';
    modalContainer.style.zIndex = '9999';
    document.body.appendChild(modalContainer);
  }
  return modalContainer;
};

const cleanupModal = () => {
  if (root) {
    root.unmount();
    root = null;
  }
  if (modalContainer && modalContainer.parentNode) {
    modalContainer.parentNode.removeChild(modalContainer);
    modalContainer = null;
  }
};

export function confirmToast(message: string, onConfirm: () => void) {
  const container = createModalContainer();
  
  const handleConfirm = () => {
    onConfirm();
    cleanupModal();
  };

  const handleCancel = () => {
    cleanupModal();
  };

  // Create root and render the modal
  if (!root) {
    root = createRoot(container);
  }
  
  root.render(
    <ConfirmModal 
      message={message} 
      onConfirm={handleConfirm} 
      onCancel={handleCancel} 
    />
  );
}
